import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Plus, Edit, Trash2, Eye, EyeOff, GripVertical, Save, X,
  HelpCircle, Shield, DollarSign, Globe, Ticket, CheckCircle
} from "lucide-react";

const CATEGORIES = [
  { id: "general", label: "Général", icon: HelpCircle },
  { id: "fraud", label: "Fraude & Sécurité", icon: Shield },
  { id: "process", label: "Processus", icon: CheckCircle },
  { id: "payment", label: "Paiement", icon: DollarSign },
  { id: "international", label: "International", icon: Globe },
  { id: "tickets", label: "Billets", icon: Ticket },
];

const FAQManager = () => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);
  const [formData, setFormData] = useState({
    question_fr: "",
    question_en: "",
    answer_fr: "",
    answer_en: "",
    category: "general",
    order: 0,
    is_active: true
  });

  useEffect(() => {
    fetchFaqs();
  }, []);

  const fetchFaqs = async () => {
    try {
      const response = await axios.get(`${API}/admin/faq`);
      setFaqs(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des FAQ");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingFaq) {
        await axios.put(`${API}/admin/faq/${editingFaq.id}`, formData);
        toast.success("FAQ mise à jour avec succès");
      } else {
        await axios.post(`${API}/admin/faq`, formData);
        toast.success("FAQ créée avec succès");
      }
      
      setShowDialog(false);
      setEditingFaq(null);
      resetForm();
      fetchFaqs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'enregistrement");
    }
  };

  const handleEdit = (faq) => {
    setEditingFaq(faq);
    setFormData({
      question_fr: faq.question_fr,
      question_en: faq.question_en,
      answer_fr: faq.answer_fr,
      answer_en: faq.answer_en,
      category: faq.category,
      order: faq.order,
      is_active: faq.is_active
    });
    setShowDialog(true);
  };

  const handleDelete = async (faqId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cette FAQ?")) return;
    
    try {
      await axios.delete(`${API}/admin/faq/${faqId}`);
      toast.success("FAQ supprimée avec succès");
      fetchFaqs();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleToggleStatus = async (faqId) => {
    try {
      const response = await axios.put(`${API}/admin/faq/${faqId}/toggle`);
      toast.success(response.data.is_active ? "FAQ activée" : "FAQ désactivée");
      fetchFaqs();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour du statut");
    }
  };

  const resetForm = () => {
    setFormData({
      question_fr: "",
      question_en: "",
      answer_fr: "",
      answer_en: "",
      category: "general",
      order: faqs.length,
      is_active: true
    });
  };

  const openNewFaqDialog = () => {
    setEditingFaq(null);
    resetForm();
    setShowDialog(true);
  };

  const getCategoryLabel = (categoryId) => {
    const cat = CATEGORIES.find(c => c.id === categoryId);
    return cat ? cat.label : categoryId;
  };

  const getCategoryIcon = (categoryId) => {
    const cat = CATEGORIES.find(c => c.id === categoryId);
    const Icon = cat ? cat.icon : HelpCircle;
    return <Icon className="h-4 w-4" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Questions fréquentes ({faqs.length})</h2>
          <p className="text-sm text-gray-500">
            Gérez les FAQ affichées sur le site public
          </p>
        </div>
        <Button onClick={openNewFaqDialog} className="bg-brand-600 hover:bg-brand-700">
          <Plus className="h-4 w-4 mr-2" />
          Ajouter une FAQ
        </Button>
      </div>

      {/* FAQ List */}
      {faqs.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <HelpCircle className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">Aucune FAQ pour le moment</p>
            <Button onClick={openNewFaqDialog} variant="outline" className="mt-4">
              <Plus className="h-4 w-4 mr-2" />
              Créer la première FAQ
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {faqs.map((faq, index) => (
            <Card key={faq.id} className={`${!faq.is_active ? 'opacity-60' : ''}`}>
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 cursor-move text-gray-400 hover:text-gray-600">
                    <GripVertical className="h-5 w-5" />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant="outline" className="flex items-center gap-1">
                        {getCategoryIcon(faq.category)}
                        {getCategoryLabel(faq.category)}
                      </Badge>
                      {!faq.is_active && (
                        <Badge variant="secondary">Désactivée</Badge>
                      )}
                    </div>
                    
                    <h3 className="font-medium text-gray-900 mb-1">
                      {faq.question_fr}
                    </h3>
                    <p className="text-sm text-gray-500 line-clamp-2">
                      {faq.answer_fr}
                    </p>
                    
                    {faq.question_en && (
                      <p className="text-xs text-gray-400 mt-2 italic">
                        EN: {faq.question_en}
                      </p>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleToggleStatus(faq.id)}
                      title={faq.is_active ? "Désactiver" : "Activer"}
                    >
                      {faq.is_active ? (
                        <Eye className="h-4 w-4 text-green-600" />
                      ) : (
                        <EyeOff className="h-4 w-4 text-gray-400" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(faq)}
                    >
                      <Edit className="h-4 w-4 text-blue-600" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(faq.id)}
                    >
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingFaq ? "Modifier la FAQ" : "Nouvelle FAQ"}
            </DialogTitle>
            <DialogDescription>
              Remplissez les informations en français et en anglais
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Category */}
            <div>
              <Label>Catégorie</Label>
              <Select 
                value={formData.category} 
                onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>
                      <span className="flex items-center gap-2">
                        <cat.icon className="h-4 w-4" />
                        {cat.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* French Question */}
            <div>
              <Label>Question (Français) *</Label>
              <Input
                value={formData.question_fr}
                onChange={(e) => setFormData(prev => ({ ...prev, question_fr: e.target.value }))}
                placeholder="Ex: Comment fonctionne le transfert sécurisé?"
                required
              />
            </div>

            {/* French Answer */}
            <div>
              <Label>Réponse (Français) *</Label>
              <Textarea
                value={formData.answer_fr}
                onChange={(e) => setFormData(prev => ({ ...prev, answer_fr: e.target.value }))}
                placeholder="Expliquez en détail..."
                rows={4}
                required
              />
            </div>

            {/* English Question */}
            <div>
              <Label>Question (Anglais)</Label>
              <Input
                value={formData.question_en}
                onChange={(e) => setFormData(prev => ({ ...prev, question_en: e.target.value }))}
                placeholder="Ex: How does secure transfer work?"
              />
            </div>

            {/* English Answer */}
            <div>
              <Label>Réponse (Anglais)</Label>
              <Textarea
                value={formData.answer_en}
                onChange={(e) => setFormData(prev => ({ ...prev, answer_en: e.target.value }))}
                placeholder="Explain in detail..."
                rows={4}
              />
            </div>

            {/* Active Status */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                className="rounded border-gray-300"
              />
              <Label htmlFor="is_active" className="cursor-pointer">
                FAQ active (visible sur le site)
              </Label>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                <X className="h-4 w-4 mr-2" />
                Annuler
              </Button>
              <Button type="submit" className="bg-brand-600 hover:bg-brand-700">
                <Save className="h-4 w-4 mr-2" />
                {editingFaq ? "Enregistrer" : "Créer"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FAQManager;
